import React, { useMemo } from 'react';

import { useSelector } from 'react-redux';

import FabricAnalyticsListeners, {
  FabricChannel,
} from '@atlaskit/analytics-listeners';
import {
  AnalyticsContext,
  AnalyticsListener,
  UIAnalyticsEvent,
} from '@atlaskit/analytics-next';

import { useConversationsContext } from 'src/contexts/conversations-context';
import { getConversationAnalytics } from 'src/selectors/conversation-selectors';
import { analyticsClient } from 'src/utils/analytics/client';

type ConversationProps = {
  id?: string;
  portalParentId: string;
};

const onEvent = (e: UIAnalyticsEvent, channel: string) => {
  const fabricChannels = Object.values(FabricChannel).map(ch => ch as string);
  const moreAttributes = (e.context || []).reduce(
    (acc, current) => ({ ...acc, ...current }),
    {}
  );

  if (channel && fabricChannels.includes(channel)) {
    // enrich attributes using analytics context
    e.payload.attributes = {
      ...e.payload.attributes,
      ...moreAttributes,
    };
  }

  if (channel === FabricChannel.editor) {
    e.payload.source = moreAttributes.source || 'unknown';
  }
};

export function withConversationAnalytics<T extends ConversationProps>(
  /* eslint @typescript-eslint/ban-types: "warn" */
  WrappedComponent: React.FC<T>
) {
  return (props: T) => {
    const attributes = useSelector(getConversationAnalytics);
    const conversationsContext = useConversationsContext();
    const source = conversationsContext.editorSource;

    return useMemo(() => {
      const { id, portalParentId } = props;
      const analyticsData = {
        ...attributes,
        conversationId: id || null,
        portalParentId,
        source,
      };

      return (
        <FabricAnalyticsListeners client={analyticsClient()}>
          <AnalyticsListener channel="*" onEvent={onEvent}>
            <AnalyticsContext data={analyticsData}>
              <WrappedComponent {...props} />
            </AnalyticsContext>
          </AnalyticsListener>
        </FabricAnalyticsListeners>
      );
    }, [props, attributes, source]);
  };
}

export enum EditorSource {
  COMMIT_CONVERSATION = 'commit-conversation',
  PR_CONVERSATION = 'pr-conversation',
  CREATE_PR_DESC = 'create-pr-desc',
  PR_DESC = 'pr-desc',
}

export function withDescriptionAnalytics<T extends {}>(
  WrappedComponent: React.ComponentType<T>,
  source: EditorSource
) {
  return (props: T) => {
    const analyticsData = {
      source,
    };

    return (
      <FabricAnalyticsListeners client={analyticsClient()}>
        <AnalyticsListener channel={FabricChannel.editor} onEvent={onEvent}>
          <AnalyticsContext data={analyticsData}>
            <WrappedComponent {...props} />
          </AnalyticsContext>
        </AnalyticsListener>
      </FabricAnalyticsListeners>
    );
  };
}
